import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { isErrorResponse, getErrorFromErrorResponse, } from '@standardnotes/snjs';
import { useState, useEffect, useRef, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import Spinner from '@/Components/Spinner/Spinner';
import Button from '@/Components/Button/Button';
import Icon from '../Icon/Icon';
import Modal from '../Modal/Modal';
import ModalOverlay from '../Modal/ModalOverlay';
import AlertDialog from '../AlertDialog/AlertDialog';
import { formatSessionAccessDate } from '@/Utils/LocalizedDateFormat';
import { c } from 'ttag';
function useSessions(application) {
    const [sessions, setSessions] = useState([]);
    const [lastRefreshDate, setLastRefreshDate] = useState(Date.now());
    const [refreshing, setRefreshing] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    useEffect(() => {
        ;
        (async () => {
            var _a, _b, _c;
            setRefreshing(true);
            const response = await application.getSessions();
            if (isErrorResponse(response)) {
                if ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.message) {
                    setErrorMessage((_c = response.data) === null || _c === void 0 ? void 0 : _c.error.message);
                }
                else {
                    setErrorMessage(c('B1.Account.Session.Error').t `An unknown error occured while loading sessions.`);
                }
            }
            else {
                const sessions = response.data;
                setSessions(sessions);
                setErrorMessage('');
            }
            setRefreshing(false);
        })().catch(console.error);
    }, [application, lastRefreshDate]);
    function refresh() {
        setLastRefreshDate(Date.now());
    }
    async function revokeSession(uuid) {
        const sessionsBeforeRevoke = sessions;
        const responsePromise = application.revokeSession(uuid);
        const sessionsDuringRevoke = sessions.slice();
        const toRemoveIndex = sessions.findIndex((session) => session.uuid === uuid);
        sessionsDuringRevoke[toRemoveIndex] = {
            ...sessionsDuringRevoke[toRemoveIndex],
            revoking: true,
        };
        setSessions(sessionsDuringRevoke);
        const response = await responsePromise;
        if (!response) {
            setSessions(sessionsBeforeRevoke);
        }
        else if (isErrorResponse(response)) {
            setErrorMessage(getErrorFromErrorResponse(response).message ||
                c('B1.Account.Session.Error').t `An unknown error occured while revoking the session.`);
            setSessions(sessionsBeforeRevoke);
        }
        else {
            setSessions(sessions.filter((session) => session.uuid !== uuid));
        }
    }
    return [sessions, refresh, refreshing, revokeSession, errorMessage];
}
const SessionsModalContent = ({ application }) => {
    const [sessions, refresh, refreshing, revokeSession, errorMessage] = useSessions(application);
    const [confirmRevokingSessionUuid, setRevokingSessionUuid] = useState('');
    const closeRevokeSessionAlert = () => setRevokingSessionUuid('');
    const cancelRevokeRef = useRef(null);
    const closeRevokeConfirmationDialog = () => {
        setRevokingSessionUuid('');
    };
    const sessionModalActions = useMemo(() => [
        {
            label: c('B1.Account.Session.Action').t `Close`,
            onClick: application.closeSessionsModal,
            type: 'cancel',
            mobileSlot: 'left',
        },
        {
            label: c('B1.Account.Session.Action').t `Refresh`,
            onClick: refresh,
            type: 'primary',
            mobileSlot: 'right',
        },
    ], [refresh, application.closeSessionsModal]);
    return (_jsxs(_Fragment, { children: [_jsx(Modal, { title: c('B1.Account.Session.Title').t `Active Sessions`, close: application.closeSessionsModal, actions: sessionModalActions, children: _jsx("div", { className: "px-4 py-4", children: refreshing ? (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Spinner, { className: "h-3 w-3" }), _jsx("h2", { className: "sk-p sessions-modal-refreshing", children: c('B1.Account.Session.Status').t `Loading sessions` })] })) : (_jsxs(_Fragment, { children: [errorMessage && (_jsx("div", { role: "alert", className: "sk-p bold", children: errorMessage })), sessions.length > 0 && (_jsx("ul", { children: sessions.map((session) => {
                                    const signedInDate = (_jsx("span", { className: "font-bold", children: formatSessionAccessDate(new Date(session.created_at)) }));
                                    return (_jsxs("li", { children: [_jsx("h2", { className: "text-base font-bold", children: session.device_info }), session.current ? (_jsx("span", { className: "font-bold text-info", children: c('B1.Account.Session.Label')
                                                    .t `Current session` })) : (_jsxs(_Fragment, { children: [_jsx("p", { children: c('B1.Account.Session.Info').jt `Signed in on ${signedInDate}` }), _jsx(Button, { primary: true, small: true, colorStyle: "danger", disabled: session.revoking, onClick: () => setRevokingSessionUuid(session.uuid), children: _jsx("span", { children: c('B1.Account.Session.Action').t `Revoke` }) })] }))] }, session.uuid));
                                }) }))] })) }) }), confirmRevokingSessionUuid && (_jsxs(AlertDialog, { closeDialog: closeRevokeConfirmationDialog, children: [_jsxs("div", { className: "flex items-center justify-between text-lg font-bold", children: [c('B1.Account.Session.Title').t `Revoke this session?`, _jsx("button", { className: "rounded p-1 font-bold hover:bg-contrast", onClick: closeRevokeConfirmationDialog, children: _jsx(Icon, { type: "close" }) })] }), _jsx("div", { className: "sk-panel-row", children: _jsx("p", { className: "text-base text-foreground lg:text-sm", children: c('B1.Account.Session.Info')
                                .t `The associated app will be signed out and all data removed from the device when it is next launched. You can sign back in on that device at any time.` }) }), _jsxs("div", { className: "mt-4 flex justify-end gap-2", children: [_jsx(Button, { ref: cancelRevokeRef, onClick: closeRevokeSessionAlert, children: _jsx("span", { children: c('B1.Account.Session.Action').t `Cancel` }) }), _jsx(Button, { primary: true, colorStyle: "danger", onClick: () => {
                                    closeRevokeSessionAlert();
                                    revokeSession(confirmRevokingSessionUuid).catch(console.error);
                                }, children: _jsx("span", { children: c('B1.Account.Session.Action').t `Revoke` }) })] })] }))] }));
};
const SessionsModal = ({ application }) => {
    return (_jsx(ModalOverlay, { isOpen: application.isSessionsModalVisible, close: application.closeSessionsModal, className: "sessions-modal", children: _jsx(SessionsModalContent, { application: application }) }));
};
export default observer(SessionsModal);
